// Shared sort state for the ranked chart/list widgets. Each option
// declares how to read its orderable value off a row (`get`); the hook
// owns the active metric + direction and hands back a comparator to
// sort rows BEFORE the top-N slice. State is in-memory (resets on
// reload) -- matches the rest of the dashboard's widget-local UI state.
import { useMemo, useState } from 'react'
import { isNil } from 'lodash'

const isNilValue = (value) => isNil(value) || Number.isNaN(value)

// options: [{ key, label, get: (row) => number | string }]
export const useChartSort = (options, { defaultKey, defaultDir = 'desc' } = {}) => {
  const [sortKey, setSortKey] = useState(defaultKey ?? options[0]?.key)
  const [sortDir, setSortDir] = useState(defaultDir)

  const active = options.find((option) => option.key === sortKey) || options[0]

  const comparator = useMemo(() => {
    const read = active?.get || (() => null)
    return (rowA, rowB) => {
      const valueA = read(rowA)
      const valueB = read(rowB)
      // Missing values always sink, regardless of direction.
      if (isNilValue(valueA) && isNilValue(valueB)) {
        return 0
      }
      if (isNilValue(valueA)) {
        return 1
      }
      if (isNilValue(valueB)) {
        return -1
      }
      const raw =
        typeof valueA === 'string' || typeof valueB === 'string'
          ? String(valueA).localeCompare(String(valueB))
          : valueA - valueB
      return sortDir === 'asc' ? raw : -raw
    }
  }, [active, sortDir])

  return {
    sortKey,
    sortDir,
    options,
    comparator,
    setMetric: setSortKey,
    toggleDir: () => setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc')),
  }
}
